<template>
	<v-container class="pa-0 mt-6">
		<v-card elevation="0" class="ant-card px-4 pb-2">
			<!-- header -->
			<v-card-title class="pa-0">
				<v-col cols="auto" class="d-flex align-center">
					<div class="v-typography--title1">
						{{ $t("product_images") }}
					</div>
				</v-col>

				<v-spacer />

				<v-col cols="auto" class="d-flex justify-end px-0">
					<!-- delete / cancel / save -->
					<v-btn v-if="!deleteMode" text outlined class="mx-1" @click="enableDelete">
						<v-icon size="17" color="red">icon-delete</v-icon>
						<span class="ml-1 v-typography--body-sm">{{ $t("image_delete") }}</span>
					</v-btn>

					<v-btn v-if="deleteMode" text outlined class="mx-1" @click="cancelDelete">
						<v-icon size="15">icon-cross</v-icon>
						<span class="ml-1 v-typography--body-sm">{{ $t("cancel") }}</span>
					</v-btn>

					<v-btn v-if="deleteMode" text outlined class="mx-1" @click="saveDelete">
						<v-icon size="15">icon-check</v-icon>
						<span class="ml-1 v-typography--body-sm">{{ $t("save") }}</span>
					</v-btn>

					<!-- add images -->
					<v-btn text outlined class="mx-1" @click="openFileDialog">
						<v-icon size="15">icon-plus</v-icon>
						<span class="ml-1 v-typography--body-sm">{{ $t("add_images") }}</span>
					</v-btn>
					<input
						ref="fileInput"
						type="file"
						multiple
						style="display: none"
						accept="image/*"
						@change="handleFileSelected"
					/>
				</v-col>
			</v-card-title>

			<v-divider class="mb-4" />

			<!-- thumbnails / empty-state -->
			<v-form v-model="valid" ref="form" class="py-0">
				<!-- no images -->
				<v-row v-if="imagesLoaded.length === 0">
					<v-col class="text-center mt-4">
						<v-card elevation="0" class="no-data-message text-center ant-card py-3">
							<v-icon class="icon-empty-state" size="28" color="black" />
							<v-col>
								<p class="font-weight-bold mb-1 black--text v-typography--body-sm">
									{{ $t("no_entity", { entityName: $t("images") }) }}
								</p>
								<span class="v-typography--body-sm no-data-message">
									{{ $t("no_entity_message", { entityName: $t("images") }) }}
								</span>
							</v-col>
						</v-card>
					</v-col>
				</v-row>

				<!-- with images -->
				<v-row v-else>
					<v-col
						v-for="(url, idx) in imagesLoaded"
						:key="idx"
						cols="auto"
						class="pa-4 pt-0 d-flex flex-column align-center"
					>
						<v-img :src="url" width="140" height="140" class="pointer" @click="openGallery(idx)" />
						<v-checkbox
							v-if="deleteMode"
							v-model="selectedForDelete"
							:value="idx"
							class="mt-2"
							dense
							hide-details
							on-icon="icon-checkbox-checked"
							off-icon="icon-checkbox-unchecked"
						/>
					</v-col>
				</v-row>
			</v-form>
		</v-card>

		<!-- full-screen gallery -->
		<div
			v-if="showGallery"
			class="fullscreen-overlay d-flex flex-column justify-center align-center"
			@click="closeOnBackground"
		>
			<v-btn icon class="gallery-close-btn" @click.stop="closeGallery">
				<v-icon color="white">icon-cross</v-icon>
			</v-btn>
			<v-btn icon class="gallery-arrow-left" @click.stop="prevImage">
				<v-icon color="white">icon-angle-left</v-icon>
			</v-btn>
			<v-btn icon class="gallery-arrow-right" @click.stop="nextImage">
				<v-icon color="white">icon-angle-right</v-icon>
			</v-btn>

			<v-img :src="imagesLoaded[currentImageIndex]" max-width="90%" max-height="90%" contain @click.stop />
		</div>
	</v-container>
</template>

<script>
import remote from "@/data/remote";

export default {
	name: "ProductImages",

	props: {
		productId: { type: String, required: true },
		images: { type: Array, default: () => [] },
	},

	data() {
		return {
			valid: true,
			deleteMode: false,
			localKeys: [],
			imagesLoaded: [],
			selectedForDelete: [],
			showGallery: false,
			currentImageIndex: 0,
		};
	},

	created() {
		this.localKeys = [...this.images];
		this.fetchAll();
	},

	watch: {
		images(val) {
			this.localKeys = [...val];
			this.fetchAll();
		},
	},

	methods: {
		/* ---------- fetch ---------- */
		async fetchAll() {
			this.imagesLoaded = [];
			for (const key of this.localKeys) {
				try {
					const blob = await this.fetchBlob(key);
					this.imagesLoaded.push(URL.createObjectURL(blob));
				} catch (e) {
					console.error("Failed to load product image:", e);
				}
			}
		},
		fetchBlob(key) {
			return new Promise((res, rej) => {
				remote.files.get(key, {
					onSuccess: (r) => res(new Blob([r])),
					onFail: (e) => rej(e),
				});
			});
		},

		/* ---------- upload ---------- */
		openFileDialog() {
			this.$refs.fileInput.click();
		},
		handleFileSelected(e) {
			const files = [...e.target.files];
			if (files.length) this.uploadFiles(files);
			e.target.value = null;
		},
		async uploadFiles(files) {
			try {
				const newKeys = [];
				for (const f of files) {
					newKeys.push(await this.uploadOne(f));
				}
				const updated = [...this.localKeys, ...newKeys];
				await this.sync(updated);
				// removed manual this.localKeys and this.fetchAll()
			} catch (e) {
				console.error("Upload error:", e);
			}
		},
		uploadOne(file) {
			return new Promise((res, rej) => {
				remote.files.send(file, "productImages", {
					onSuccess: (r) => res(r.key),
					onFail: (e) => rej(e),
				});
			});
		},

		/* ---------- delete ---------- */
		enableDelete() {
			this.deleteMode = true;
			this.selectedForDelete = [];
		},
		cancelDelete() {
			this.deleteMode = false;
			this.selectedForDelete = [];
		},
		async saveDelete() {
			try {
				const updated = this.localKeys.filter((_, i) => !this.selectedForDelete.includes(i));
				await this.sync(updated);
				// removed manual this.localKeys and this.fetchAll()
				this.deleteMode = false;
				this.selectedForDelete = [];
			} catch (e) {
				console.error("Delete error:", e);
			}
		},

		/* ---------- server sync ---------- */
		sync(keys) {
			return new Promise((res, rej) => {
				remote.products.updateImages(this.productId, keys, {
					onSuccess: (p) => {
						this.$emit("refresh-product", p);
						res(p);
					},
					onFail: (e) => rej(e),
				});
			});
		},

		/* ---------- gallery ---------- */
		openGallery(i) {
			this.currentImageIndex = i;
			this.showGallery = true;
			window.addEventListener("keydown", this.onKey);
		},
		closeGallery() {
			this.showGallery = false;
			window.removeEventListener("keydown", this.onKey);
		},
		closeOnBackground() {
			this.closeGallery();
		},
		prevImage() {
			this.currentImageIndex = (this.currentImageIndex - 1 + this.imagesLoaded.length) % this.imagesLoaded.length;
		},
		nextImage() {
			this.currentImageIndex = (this.currentImageIndex + 1) % this.imagesLoaded.length;
		},
		onKey(e) {
			if (e.key === "ArrowLeft") this.prevImage();
			if (e.key === "ArrowRight") this.nextImage();
			if (e.key === "Escape") this.closeGallery();
		},
	},
};
</script>

<style scoped>
.fullscreen-overlay {
	position: fixed;
	inset: 0;
	background: rgba(0, 0, 0, 0.8);
	z-index: 9999;
	cursor: pointer;
}
.gallery-close-btn {
	position: absolute;
	top: 50px;
	right: 100px;
}
.gallery-arrow-left {
	position: absolute;
	top: 50%;
	left: 100px;
	transform: translateY(-50%);
}
.gallery-arrow-right {
	position: absolute;
	top: 50%;
	right: 100px;
	transform: translateY(-50%);
}
.pointer {
	cursor: pointer;
}
@media (max-width: 600px) {
	.gallery-close-btn {
		top: 2%;
		right: 2%;
	}
	.gallery-arrow-left {
		left: 2%;
	}
	.gallery-arrow-right {
		right: 2%;
	}
}
</style>
